<template>
  <div class="audio-player">
    <div class="controls-row" ref="row">
      <v-btn
        ref="btn"
        icon
        variant="text"
        :color="btnColor"
        :size="btnSize"
        :ripple="false"
        @click="togglePlay"
        density="comfortable"
      >
        <v-icon :icon="isPlaying ? 'mdi-pause-circle-outline' : 'mdi-play-circle-outline'"></v-icon>
      </v-btn>
      <canvas ref="canvas" :style="{ height: canvHeight + 'px', width: '100%' }" />
    </div>
    <audio ref="player" :src="src" :controls="false" class="hidden-audio" crossorigin="anonymous" preload="auto" />
  </div>
 </template>

<script lang="ts">
import { ref, type Ref, type ComponentPublicInstance, nextTick } from 'vue'
import { Options, Vue } from 'vue-class-component'
import { useAVLine } from 'vue-audio-visual'

type BarColor = string | string[]

@Options({
  components: { },
  props: {
    src: { type: String, required: true },
    canvHeight: { type: Number, default: 40 },
    canvWidth: { type: Number, default: 300 },
    barColor: { type: [String, Array], default: '#64B5F6' },
    showControls: { type: Boolean, default: false },
    corsAnonym: { type: Boolean, default: true },
    btnColor: { type: String, default: 'primary' },
    btnSize: { type: String, default: 'large' },
    fftSize: { type: Number, default: 512 },
    autoPlay: { type: Boolean, default: false }
  }
})
export default class AudioPlayer extends Vue {
  // Props
  src!: string
  canvHeight!: number
  canvWidth!: number
  barColor!: BarColor
  showControls!: boolean
  corsAnonym!: boolean
  btnColor!: string
  btnSize!: string
  fftSize!: number
  autoPlay!: boolean

  // playback state
  private playing_ = false

  // AV instance and DOM-backed refs for reactive re-init
  private avInstance: unknown | null = null
  private prevSrc_: string | null = null

  get isPlaying(): boolean {
    return this.playing_
  }

  declare $refs: {
    row: HTMLDivElement
    btn: ComponentPublicInstance | HTMLElement
    player: HTMLAudioElement
    canvas: HTMLCanvasElement
  }

  // responsive width handling
  private resizeObserver?: ResizeObserver
  private lineWidth = 300

  private getAvailableCanvasWidth(): number {
    const canvas = this.$refs?.canvas
    if (!canvas) return this.canvWidth
    // Use rendered width of the flexed canvas to avoid Vuetify internal paddings/gaps/DOM structure
    const w = canvas.getBoundingClientRect().width
    return w > 0 ? w : this.canvWidth
  }

  private setCanvasSize(width: number, height: number): void {
    const canvas = this.$refs?.canvas
    if (!canvas) return
    const w = Math.max(0, Math.floor(width))
    const h = Math.max(0, Math.floor(height))
    canvas.width = w
    canvas.height = h
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
  }

  private initVisualizer(): void {
    const player = this.$refs?.player
    const canvas = this.$refs?.canvas
    if (!player || !canvas) return

    // Bind refs used by useAVLine (local Refs per init)
    const playerRef = ref<HTMLAudioElement | null>(player)
    const canvasRef = ref<HTMLCanvasElement | null>(canvas)

    const width = this.getAvailableCanvasWidth() - 20
    this.setCanvasSize(width, this.canvHeight)

    // Cleanup any previous instance if library provides a method
    const prev = this.avInstance as { destroy?: () => void; stop?: () => void } | null
    if (prev?.destroy) {
      try { prev.destroy() } catch { /* ignore */ }
    } else if (prev?.stop) {
      try { prev.stop() } catch { /* ignore */ }
    }

    // Initialize visualizer
    this.avInstance = useAVLine(playerRef as unknown as never, canvasRef as unknown as never, {
      src: this.src,
      canvHeight: this.canvHeight,
      canvWidth: width,
      lineColor: this.barColor,
      fftSize: this.fftSize,
      audioControls: false,
      corsAnonym: this.corsAnonym
    })
  }

  mounted(): void {
    // Bind audio events to keep icon state in sync and ensure a user gesture controls playback
    const audio = this.$refs.player
    if (audio) {
      audio.addEventListener('play', this.onPlay)
      audio.addEventListener('pause', this.onPause)
      audio.addEventListener('ended', this.onEnded)
    }

    // Defer sizing/initialization to the next microtask so Vuetify can finalize layout
    nextTick(() => {
      this.initVisualizer()
      this.prevSrc_ = this.src
      if (this.autoPlay) this.playAudio()
    })

    // Observe row width changes to keep canvas responsive
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        const w = this.getAvailableCanvasWidth() - 20
        this.lineWidth = w
        this.setCanvasSize(w, this.canvHeight)
      })
      this.resizeObserver.observe(this.$refs.row)
    }
  }

  beforeUnmount(): void {
    const audio = this.$refs.player
    if (audio) {
      audio.removeEventListener('play', this.onPlay)
      audio.removeEventListener('pause', this.onPause)
      audio.removeEventListener('ended', this.onEnded)
    }

    // Cleanup visualizer instance if supported
    const prev = this.avInstance as { destroy?: () => void; stop?: () => void } | null
    if (prev?.destroy) {
      try { prev.destroy() } catch { /* ignore */ }
    } else if (prev?.stop) {
      try { prev.stop() } catch { /* ignore */ }
    }
    this.avInstance = null

    if (this.resizeObserver) {
      try {
        this.resizeObserver.disconnect()
      } catch { /* ignore */ }
      this.resizeObserver = undefined
    }
  }

  private onPlay = (): void => {
    this.playing_ = true
  }

  private onPause = (): void => {
    this.playing_ = false
  }

  private onEnded = (): void => {
    this.playing_ = false
    // rewind to start for next play (guard ref in case component is tearing down)
    const audio = this.$refs?.player
    if (audio) {
      try {
        audio.currentTime = 0
      } catch {
        /* ignore */
      }
    }
    // clear visualization
    this.resetCanvas()
  }

  private resetCanvas(): void {
    const canvas = this.$refs?.canvas
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }


  updated(): void {
    // If the audio source changed, avoid re-creating a MediaElementSourceNode for the same element.
    // Only control playback; the existing visualizer remains connected to the <audio> element.
    if (this.src !== this.prevSrc_) {
      this.prevSrc_ = this.src
      if (this.autoPlay) this.playAudio()
    }
  }

  private playAudio(): void {
    const audio = this.$refs.player
    if (!audio) return
    const p = audio.play()
    if (p && typeof p.then === 'function') {
      p.catch(() => { /* ignored: autoplay may be blocked without gesture */ })
    }
  }

  togglePlay(): void {
    const audio = this.$refs.player
    if (!audio) return
    if (audio.paused) {
      const p = audio.play()
      if (p && typeof p.then === 'function') {
        p.catch(() => { /* ignored: autoplay policy requires user gesture */ })
      }
    } else {
      audio.pause()
    }
  }


}
</script>

<style scoped>
.audio-player {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
}

.controls-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.controls-row canvas {
  display: block;
  flex: 1 1 auto;
}

.hidden-audio {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
